/**
 * Stagioni (V46): elenco, creazione, modifica, stagione corrente.
 *
 * Una sola stagione e corrente per volta e cambiarla sposta il contesto di
 * tutti: selettore e ingresso vanno ricaricati. Dopo la creazione si propone
 * il passo successivo, gli incroci con le squadre; l'import delle rose
 * arrivera con la sua vista.
 */

import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleCheckIcon, OctagonXIcon, PencilIcon } from 'lucide-react';
import { stagioneSchema } from '@calcio/shared';
import { toast } from 'sonner';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MenuAzioni, PulsanteInvio, PulsanteNuovo } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { useContesto } from '../navigazione/contesto.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { dataBreve, erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import type { Stagione } from './matrice.js';
import { CampoData } from '../comuni/CampoData.js';

/** Null: dialog chiuso. Senza id: nuova stagione. */
type InModifica = Partial<Stagione> | null;

export function Stagioni() {
  const { dati: stagioni, errore, ricarica } = useDati<Stagione[]>('/amministrazione/stagioni');
  const { ricaricaSquadre } = useContesto();
  const [inModifica, setInModifica] = useState<InModifica>(null);
  const [creata, setCreata] = useState<string | null>(null);

  const nuova = <PulsanteNuovo etichetta="Nuova stagione" onClick={() => setInModifica({})} />;

  if (!stagioni)
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );

  const rendiCorrente = async (s: Stagione) => {
    try {
      await chiama(`/amministrazione/stagioni/${s.id}/corrente`, { metodo: 'POST' });
      await Promise.all([ricarica(), ricaricaSquadre()]);
      toast.success(`${s.nome} è la stagione corrente.`);
    } catch (e) {
      toast.error(messaggio(e));
    }
  };

  return (
    <>
      <Testata titolo="Stagioni" voce="stagioni" azione={stagioni.length > 0 && nuova} />
      <Corpo>
        <div className="flex flex-col gap-4">
          {creata && (
            <Alert>
              <CircleCheckIcon />
              <AlertTitle>Stagione {creata} creata</AlertTitle>
              <AlertDescription>
                Il passo successivo è aggiungere le squadre che giocano questa stagione.
              </AlertDescription>
              <AlertAction>
                <Button asChild size="sm" variant="outline">
                  <Link to="/amministrazione/squadre-stagione">Squadre-stagione</Link>
                </Button>
              </AlertAction>
            </Alert>
          )}

          {stagioni.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>Nessuna stagione</EmptyTitle>
                <EmptyDescription>
                  Crea la prima stagione: squadre, rose e partite si agganciano a lei.
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>{nuova}</EmptyContent>
            </Empty>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Periodo</TableHead>
                  <TableHead className="text-right">Squadre</TableHead>
                  <TableHead>
                    <span className="sr-only">Azioni</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stagioni.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-2">
                        {s.nome}
                        {s.corrente && <Badge>Corrente</Badge>}
                      </span>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {dataBreve(s.dataInizio)} – {dataBreve(s.dataFine)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{s.squadre}</TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <MenuAzioni
                          riga
                          etichetta={`Azioni su ${s.nome}`}
                          voci={[
                            {
                              etichetta: 'Modifica',
                              Icona: PencilIcon,
                              onSelect: () => setInModifica(s),
                            },
                            {
                              etichetta: 'Imposta corrente',
                              Icona: CircleCheckIcon,
                              disabled: s.corrente,
                              motivo: 'È già la stagione corrente.',
                              onSelect: () => void rendiCorrente(s),
                            },
                          ]}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogStagione
          stagione={inModifica}
          onChiudi={() => setInModifica(null)}
          onSalvata={async (nome, nuova) => {
            setInModifica(null);
            setCreata(nuova ? nome : null);
            await Promise.all([ricarica(), ricaricaSquadre()]);
            if (!nuova) toast.success(`Stagione ${nome} salvata.`);
          }}
        />
      </Corpo>
    </>
  );
}

function DialogStagione({
  stagione,
  onChiudi,
  onSalvata,
}: {
  stagione: InModifica;
  onChiudi: () => void;
  onSalvata: (nome: string, nuova: boolean) => Promise<void>;
}) {
  return (
    <Dialog open={stagione !== null} onOpenChange={(aperto) => !aperto && onChiudi()}>
      <DialogContent>
        {/* Rimontato a ogni apertura: il form parte dai valori della riga scelta. */}
        {stagione && (
          <FormStagione
            key={stagione.id ?? 'nuova'}
            stagione={stagione}
            onChiudi={onChiudi}
            onSalvata={onSalvata}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormStagione({
  stagione,
  onChiudi,
  onSalvata,
}: {
  stagione: Partial<Stagione>;
  onChiudi: () => void;
  onSalvata: (nome: string, nuova: boolean) => Promise<void>;
}) {
  const [valori, setValori] = useState({
    nome: stagione.nome ?? '',
    dataInizio: stagione.dataInizio ?? '',
    dataFine: stagione.dataFine ?? '',
  });
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = stagioneSchema.safeParse(valori);
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      if (stagione.id) {
        await chiama(`/amministrazione/stagioni/${stagione.id}`, {
          metodo: 'PUT',
          corpo: esito.data,
        });
      } else {
        await chiama('/amministrazione/stagioni', { metodo: 'POST', corpo: esito.data });
      }
      await onSalvata(esito.data.nome, !stagione.id);
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  const campo = (nome: keyof typeof valori, etichetta: string) => (
    <Field data-invalid={Boolean(errori[nome])}>
      <FieldLabel htmlFor={`stagione-${nome}`}>{etichetta}</FieldLabel>
      <Input
        id={`stagione-${nome}`}
        value={valori[nome]}
        aria-invalid={Boolean(errori[nome])}
        onChange={(e) => setValori((v) => ({ ...v, [nome]: e.target.value }))}
      />
      <FieldError>{errori[nome]}</FieldError>
    </Field>
  );

  const campoData = (nome: 'dataInizio' | 'dataFine', etichetta: string, min?: string) => (
    <Field data-invalid={Boolean(errori[nome])}>
      <FieldLabel htmlFor={`stagione-${nome}`}>{etichetta}</FieldLabel>
      <CampoData
        id={`stagione-${nome}`}
        valore={valori[nome]}
        min={min}
        invalido={Boolean(errori[nome])}
        onCambia={(data) => setValori((v) => ({ ...v, [nome]: data }))}
      />
      <FieldError>{errori[nome]}</FieldError>
    </Field>
  );

  return (
    <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>{stagione.id ? `Modifica ${stagione.nome}` : 'Nuova stagione'}</DialogTitle>
        <DialogDescription>
          Per esempio 2026/27: vale per tutte le squadre della società.
        </DialogDescription>
      </DialogHeader>

      <FieldGroup>
        {campo('nome', 'Nome')}
        <div className="grid grid-cols-2 gap-4">
          {campoData('dataInizio', 'Inizio')}
          {campoData('dataFine', 'Fine', valori.dataInizio || undefined)}
        </div>
      </FieldGroup>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          {stagione.id ? 'Salva' : 'Crea stagione'}
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
